import type { Weather } from '../sim/Weather';

/**
 * Small readout for current time-of-day + weather state. Lives under the
 * minimap. Reads straight off Weather.state each frame — cheap DOM updates.
 */
export class WeatherHUD {
  readonly dom: HTMLElement;

  private timeSpan: HTMLSpanElement;
  private phaseSpan: HTMLSpanElement;
  private rainFill: HTMLDivElement;
  private windFill: HTMLDivElement;
  private calendarSpan: HTMLSpanElement;
  private tempSpan: HTMLSpanElement;
  private banner: HTMLDivElement;

  constructor(private weather: Weather) {
    const root = document.createElement('div');
    root.style.cssText = [
      'position:fixed',
      'top:300px',
      'right:8px',
      'width:180px',
      'background:rgba(15,20,25,0.78)',
      'color:#eee',
      'border-radius:8px',
      'padding:8px 10px',
      'font:11px/1.35 system-ui, sans-serif',
      'pointer-events:none',
      'z-index:5',
      'box-shadow:0 2px 8px rgba(0,0,0,0.4)',
      'display:flex',
      'flex-direction:column',
      'gap:6px',
    ].join(';');

    const header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:center';

    const timeSpan = document.createElement('span');
    timeSpan.style.cssText = 'font-weight:600;letter-spacing:0.02em';
    timeSpan.textContent = '00:00';

    const phaseSpan = document.createElement('span');
    phaseSpan.style.cssText = 'opacity:0.9';
    phaseSpan.textContent = '\u2600\uFE0F Day';

    header.appendChild(timeSpan);
    header.appendChild(phaseSpan);

    // Calendar + temperature line.
    const calRow = document.createElement('div');
    calRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;font-size:10.5px;opacity:0.88';
    const calendarSpan = document.createElement('span');
    calendarSpan.textContent = '';
    const tempSpan = document.createElement('span');
    tempSpan.textContent = '';
    tempSpan.style.cssText = 'font-variant-numeric:tabular-nums;';
    calRow.appendChild(calendarSpan);
    calRow.appendChild(tempSpan);

    const rainRow = this.makeBarRow('Rain', '#4aa3ff');
    const windRow = this.makeBarRow('Wind', '#ffffff');

    root.appendChild(header);
    root.appendChild(calRow);
    root.appendChild(rainRow.row);
    root.appendChild(windRow.row);
    this.calendarSpan = calendarSpan;
    this.tempSpan = tempSpan;

    document.body.appendChild(root);

    // Big centred banner for survival events. Hidden by default; shown
    // during cold-snap / heat-wave warning + active phases. Driven from
    // update() each frame.
    const banner = document.createElement('div');
    banner.style.cssText = [
      'position:fixed', 'top:120px', 'left:50%', 'transform:translateX(-50%)',
      'padding:14px 28px',
      'border-radius:12px',
      'font:600 18px/1.2 system-ui,sans-serif',
      'letter-spacing:0.04em',
      'pointer-events:none',
      'box-shadow:0 6px 24px rgba(0,0,0,0.55)',
      'border:1px solid rgba(255,255,255,0.15)',
      'z-index:6',
      'display:none',
      'text-align:center',
      'min-width:320px',
    ].join(';');
    document.body.appendChild(banner);
    this.banner = banner;

    this.dom = root;
    this.timeSpan = timeSpan;
    this.phaseSpan = phaseSpan;
    this.rainFill = rainRow.fill;
    this.windFill = windRow.fill;
  }

  private makeBarRow(label: string, color: string): { row: HTMLDivElement; fill: HTMLDivElement } {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:6px';

    const caption = document.createElement('span');
    caption.textContent = label;
    caption.style.cssText = 'width:34px;opacity:0.85';

    const track = document.createElement('div');
    track.style.cssText = [
      'flex:1',
      'height:4px',
      'background:rgba(255,255,255,0.12)',
      'border-radius:2px',
      'overflow:hidden',
    ].join(';');

    const fill = document.createElement('div');
    fill.style.cssText = [
      'height:100%',
      'width:0%',
      `background:${color}`,
      'border-radius:2px',
      'transition:width 120ms linear',
    ].join(';');

    track.appendChild(fill);
    row.appendChild(caption);
    row.appendChild(track);

    return { row, fill };
  }

  update(): void {
    const s = this.weather.state;

    // Clock
    const hoursFloat = s.timeOfDay * 24;
    const hh = Math.floor(hoursFloat);
    const mm = Math.floor((hoursFloat - hh) * 60);
    this.timeSpan.textContent = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;

    // Day/night label
    const isDay = s.sunIntensity > 0.1;
    this.phaseSpan.textContent = isDay ? '\u2600\uFE0F Day' : '\uD83C\uDF19 Night';

    // Rain bar
    const rain = Math.max(0, Math.min(1, s.rain));
    this.rainFill.style.width = `${(rain * 100).toFixed(1)}%`;

    // Wind bar: (wind - 1) / 3 clamped 0..1
    const windNorm = Math.max(0, Math.min(1, (s.wind - 1) / 3));
    this.windFill.style.width = `${(windNorm * 100).toFixed(1)}%`;

    // Calendar + temperature.
    this.calendarSpan.textContent = `${formatDate(s.dayOfYear)} · ${s.season}`;
    const tc = Math.round(s.temperature);
    this.tempSpan.textContent = `${tc}°C`;
    this.tempSpan.style.color = tc < 0 ? '#8ec5ff' : tc > 28 ? '#ff9a52' : '#d4e4c8';

    // Survival-event banner. Cold takes precedence over heat — both
    // shouldn't fire simultaneously (different season windows) but if
    // they ever overlap, the colder one is the more urgent hazard.
    let bannerText = '';
    let bannerBg = '';
    let bannerColor = '#fff';
    if (s.coldSnapPhase === 'warning') {
      bannerText = `❄ COLD SNAP INCOMING — ${Math.ceil(s.coldSnapT)}s — GET TO THE NEST`;
      bannerBg = 'rgba(60, 100, 160, 0.92)';
      bannerColor = '#e6f1ff';
    } else if (s.coldSnapPhase === 'active') {
      bannerText = `❄ COLD SNAP — ${Math.ceil(s.coldSnapT)}s remaining`;
      bannerBg = 'rgba(40, 70, 120, 0.95)';
      bannerColor = '#bcd9ff';
    } else if (s.heatWavePhase === 'warning') {
      bannerText = `🔥 HEAT WAVE INCOMING — ${Math.ceil(s.heatWaveT)}s — GET TO THE NEST`;
      bannerBg = 'rgba(180, 70, 30, 0.92)';
      bannerColor = '#fff1e0';
    } else if (s.heatWavePhase === 'active') {
      bannerText = `🔥 HEAT WAVE — ${Math.ceil(s.heatWaveT)}s remaining`;
      bannerBg = 'rgba(140, 50, 20, 0.95)';
      bannerColor = '#ffd9ad';
    }
    if (bannerText) {
      this.banner.textContent = bannerText;
      this.banner.style.background = bannerBg;
      this.banner.style.color = bannerColor;
      this.banner.style.display = 'block';
    } else {
      this.banner.style.display = 'none';
    }
  }
}

/** Convert DOY [1..365] into "MMM DD". */
function formatDate(doy: number): string {
  const months = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let d = doy;
  for (let i = 0; i < months.length; i++) {
    if (d <= months[i]) return `${names[i]} ${d}`;
    d -= months[i];
  }
  return `Dec ${months[11]}`;
}
